import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Textarea } from "@repo/o37-ui";
import { ArrowLeft, Trash2, Plus, Users, PieChart } from "lucide-react";
import { Fundraise, CapTableEntry, CAP_TABLE_TYPES, CapTableEntryType } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

export default function RaiseSettings() {
  const { fundraiseId } = useParams<{ fundraiseId: string }>();
  const navigate = useNavigate();
  const isNew = fundraiseId === 'new';

  const [fundraise, setFundraise] = useState<Partial<Fundraise>>({
    name: '',
    description: '',
    status: 'draft',
    raiseGoal: undefined,
    linkedVdrId: undefined
  });
  const [vdrRooms, setVdrRooms] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [capTable, setCapTable] = useState<CapTableEntry[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'captable' | 'vdr' | 'team'>('general');

  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('viewer');

  // Cap table form state
  const [newShareholderName, setNewShareholderName] = useState('');
  const [newShareholderType, setNewShareholderType] = useState<CapTableEntryType>('founder');
  const [newShareholderPercent, setNewShareholderPercent] = useState('');
  const [editingEntry, setEditingEntry] = useState<string | null>(null);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadVdrRooms();
    if (!isNew && fundraiseId) {
      loadFundraise();
      loadPermissions();
      loadCapTable();
    }
  }, [fundraiseId]);

  const loadFundraise = async () => {
    try {
      const response = await fetch(`${API_URL}/fundraising/raises/${fundraiseId}`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setFundraise({
          id: data.id,
          name: data.name,
          description: data.description,
          status: data.status,
          raiseGoal: data.raise_goal,
          linkedVdrId: data.linked_vdr_id
        });
      }
    } catch (e) {
      console.error('Failed to load fundraise:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadVdrRooms = async () => {
    try {
      const response = await fetch(`${API_URL}/vdr`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setVdrRooms(data || []);
      }
    } catch (e) {
      console.error('Failed to load VDR rooms:', e);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await fetch(`${API_URL}/fundraising/raises/${fundraiseId}/permissions`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setPermissions(data || []);
      }
    } catch (e) {
      console.error('Failed to load permissions:', e);
    }
  };

  const loadCapTable = async () => {
    try {
      const response = await fetch(`${API_URL}/fundraising/raises/${fundraiseId}/cap-table`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        // Transform snake_case to camelCase
        const transformed = data.map((entry: any) => ({
          id: entry.id,
          fundraiseId: entry.fundraise_id,
          name: entry.name,
          type: entry.type,
          ownershipPercentage: parseFloat(entry.ownership_percentage),
          shares: entry.shares,
          notes: entry.notes,
          isActive: entry.is_active,
          createdAt: entry.created_at,
          updatedAt: entry.updated_at
        }));
        setCapTable(transformed);
      }
    } catch (e) {
      console.error('Failed to load cap table:', e);
    }
  };

  const handleAddShareholder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newShareholderName.trim() || !newShareholderPercent) return;

    try {
      const response = await fetch(`${API_URL}/fundraising/raises/${fundraiseId}/cap-table`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({
          name: newShareholderName,
          type: newShareholderType,
          ownershipPercentage: parseFloat(newShareholderPercent)
        })
      });

      if (response.ok) {
        setNewShareholderName('');
        setNewShareholderPercent('');
        setNewShareholderType('founder');
        loadCapTable();
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to add shareholder');
      }
    } catch (e) {
      alert('Failed to add shareholder');
    }
  };

  const handleUpdateShareholder = async (entryId: string, updates: Partial<CapTableEntry>) => {
    try {
      const response = await fetch(`${API_URL}/fundraising/raises/${fundraiseId}/cap-table/${entryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        setEditingEntry(null);
        loadCapTable();
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to update shareholder');
      }
    } catch (e) {
      alert('Failed to update shareholder');
    }
  };

  const handleDeleteShareholder = async (entryId: string) => {
    if (!confirm('Remove this shareholder from the cap table?')) return;

    try {
      const response = await fetch(`${API_URL}/fundraising/raises/${fundraiseId}/cap-table/${entryId}`, {
        method: 'DELETE',
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });

      if (response.ok) {
        loadCapTable();
      }
    } catch (e) {
      alert('Failed to remove shareholder');
    }
  };

  const totalOwnership = capTable.reduce((sum, entry) => sum + entry.ownershipPercentage, 0);

  const handleSave = async () => {
    if (!fundraise.name?.trim()) {
      alert('Name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: fundraise.name,
        description: fundraise.description,
        status: fundraise.status,
        raiseGoal: fundraise.raiseGoal,
        linkedVdrId: fundraise.linkedVdrId || null
      };

      const url = isNew 
        ? `${API_URL}/fundraising/raises`
        : `${API_URL}/fundraising/raises/${fundraiseId}`;

      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        if (isNew) {
          navigate(`/fundraising/raises/${data.id}`);
        } else {
          navigate(`/fundraising/raises/${fundraiseId}`);
        }
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to save');
      }
    } catch (e) {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this fundraise? This cannot be undone.')) return;

    try {
      const response = await fetch(`${API_URL}/fundraising/raises/${fundraiseId}`, {
        method: 'DELETE',
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });

      if (response.ok) {
        navigate('/fundraising/raises');
      } else {
        alert('Failed to delete');
      }
    } catch (e) {
      alert('Failed to delete');
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserEmail.trim()) return;

    try {
      const response = await fetch(`${API_URL}/fundraising/raises/${fundraiseId}/permissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({
          email: newUserEmail,
          accessLevel: newUserRole
        })
      });

      if (response.ok) {
        setNewUserEmail('');
        loadPermissions();
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to add user');
      }
    } catch (e) {
      alert('Failed to add user');
    }
  };

  const handleRemoveUser = async (permUserId: string) => {
    if (!confirm('Remove access for this user?')) return;

    try {
      const response = await fetch(`${API_URL}/fundraising/raises/${fundraiseId}/permissions/${permUserId}`, {
        method: 'DELETE',
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });

      if (response.ok) {
        loadPermissions();
      }
    } catch (e) {
      alert('Failed to remove user');
    }
  };

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold">Loading...</h1>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          icon={<ArrowLeft className="w-4 h-4" />}
          text="Back"
          className="mb-4"
        />
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            {isNew ? 'Create Fundraise' : 'Fundraise Settings'}
          </h1>
          {!isNew && userRole === 'super_admin' && (
            <Button
              variant="danger-outline"
              onClick={handleDelete}
              icon={<Trash2 className="w-4 h-4" />}
              text="Delete"
            />
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-neutral-200">
        {(['general', 'captable', 'vdr', 'team'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-black text-black'
                : 'border-transparent text-neutral-500 hover:text-black'
            }`}
          >
            {tab === 'general' && 'General'}
            {tab === 'captable' && 'Cap Table'}
            {tab === 'vdr' && 'VDR Link'}
            {tab === 'team' && 'Team Access'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <Card>
          <CardContent className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Name *</label>
              <Input
                value={fundraise.name || ''}
                onChange={(e) => setFundraise({ ...fundraise, name: e.target.value })}
                placeholder="e.g., Series A 2024"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
              <Textarea
                value={fundraise.description || ''}
                onChange={(e) => setFundraise({ ...fundraise, description: e.target.value })}
                placeholder="Brief description of this fundraise..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Raise Goal</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                  <Input
                    value={fundraise.raiseGoal?.toString() || ''}
                    onChange={(e) => setFundraise({ ...fundraise, raiseGoal: parseFloat(e.target.value) || undefined })}
                    placeholder="40000000"
                    type="number"
                    className="pl-7"
                  />
                </div>
              </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Status</label>
              <select
                className="w-full h-10 px-3 py-2 bg-white border border-neutral-200 rounded-lg text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                value={fundraise.status || 'draft'}
                onChange={(e) => setFundraise({ ...fundraise, status: e.target.value as any })}
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            </div>

            <div className="pt-4 border-t border-neutral-100 flex justify-end">
              <Button onClick={handleSave} disabled={saving} text={saving ? 'Saving...' : 'Save Changes'} />
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'captable' && !isNew && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5" />
              Current Cap Table
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <p className="text-sm text-neutral-600">
              Enter your current equity ownership structure. This is used to calculate dilution from new investments.
            </p>

            {/* Summary Bar */}
            <div className={`p-4 rounded-lg border ${totalOwnership > 100 ? 'bg-red-50 border-red-200' : totalOwnership === 100 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Total Ownership</span>
                <span className={`text-lg font-semibold ${totalOwnership > 100 ? 'text-red-600' : totalOwnership === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {totalOwnership.toFixed(2)}%
                </span>
              </div>
              {totalOwnership !== 100 && (
                <p className="text-xs mt-1 text-neutral-500">
                  {totalOwnership > 100 ? 'Total exceeds 100%. Please adjust.' : `${(100 - totalOwnership).toFixed(2)}% unallocated`}
                </p>
              )}
            </div>

            {/* Add Shareholder Form */}
            <form onSubmit={handleAddShareholder} className="p-4 bg-neutral-50 rounded-lg border border-neutral-100">
              <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Shareholder
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input
                  placeholder="Name (e.g., John Smith)"
                  value={newShareholderName}
                  onChange={(e) => setNewShareholderName(e.target.value)}
                  className="md:col-span-1"
                />
                <select
                  className="h-10 px-3 py-2 bg-white border border-neutral-200 rounded-lg text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  value={newShareholderType}
                  onChange={(e) => setNewShareholderType(e.target.value as CapTableEntryType)}
                >
                  {CAP_TABLE_TYPES.map(({ type, label }) => (
                    <option key={type} value={type}>{label}</option>
                  ))}
                </select>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="Ownership %"
                    value={newShareholderPercent}
                    onChange={(e) => setNewShareholderPercent(e.target.value)}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">%</span>
                </div>
                <Button type="submit" text="Add" icon={<Plus className="w-4 h-4" />} />
              </div>
            </form>

            {/* Cap Table List */}
            <div>
              <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Shareholders ({capTable.length})
              </h3>
              {capTable.length === 0 ? (
                <div className="text-center py-8 text-neutral-400">
                  <PieChart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No shareholders added yet.</p>
                  <p className="text-xs mt-1">Add founders and existing investors above.</p>
                </div>
              ) : (
                <div className="border rounded-lg divide-y overflow-hidden">
                  {capTable.map((entry) => {
                    const typeConfig = CAP_TABLE_TYPES.find(t => t.type === entry.type);
                    return (
                      <div key={entry.id} className="p-3 flex items-center justify-between bg-white hover:bg-neutral-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${typeConfig?.color || 'bg-slate-400'}`} />
                          <div>
                            <div className="font-medium text-sm">{entry.name}</div>
                            <div className="text-xs text-neutral-500 capitalize">{typeConfig?.label || entry.type}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {editingEntry === entry.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                defaultValue={entry.ownershipPercentage}
                                className="w-24 h-8 text-sm"
                                onBlur={(e) => {
                                  const newVal = parseFloat(e.target.value);
                                  if (!isNaN(newVal) && newVal !== entry.ownershipPercentage) {
                                    handleUpdateShareholder(entry.id, { ownershipPercentage: newVal });
                                  } else {
                                    setEditingEntry(null);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    const newVal = parseFloat((e.target as HTMLInputElement).value);
                                    if (!isNaN(newVal)) {
                                      handleUpdateShareholder(entry.id, { ownershipPercentage: newVal });
                                    }
                                  } else if (e.key === 'Escape') {
                                    setEditingEntry(null);
                                  }
                                }}
                                autoFocus
                              />
                              <span className="text-sm text-neutral-400">%</span>
                            </div>
                          ) : (
                            <button
                              onClick={() => setEditingEntry(entry.id)}
                              className="text-lg font-semibold text-neutral-900 hover:text-blue-600 transition-colors"
                            >
                              {entry.ownershipPercentage.toFixed(2)}%
                            </button>
                          )}
                          <Button
                            variant="danger-outline"
                            size="sm"
                            onClick={() => handleDeleteShareholder(entry.id)}
                            icon={<Trash2 className="w-3 h-3" />}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Visual Breakdown by Type */}
            {capTable.length > 0 && (
              <div className="pt-4 border-t border-neutral-100">
                <h4 className="text-xs uppercase tracking-wider text-neutral-500 font-medium mb-3">Ownership by Type</h4>
                <div className="space-y-2">
                  {CAP_TABLE_TYPES.map(({ type, label, color }) => {
                    const typeTotal = capTable
                      .filter(e => e.type === type)
                      .reduce((sum, e) => sum + e.ownershipPercentage, 0);
                    if (typeTotal === 0) return null;
                    return (
                      <div key={type} className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${color}`} />
                        <span className="text-sm text-neutral-600 flex-1">{label}</span>
                        <div className="w-32 h-2 bg-neutral-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${color}`}
                            style={{ width: `${Math.min(typeTotal, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium w-16 text-right">{typeTotal.toFixed(1)}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'captable' && isNew && (
        <Card>
          <CardContent className="p-6 text-center text-neutral-500">
            Save the fundraise first to manage the cap table.
          </CardContent>
        </Card>
      )}

      {activeTab === 'vdr' && (
        <Card>
          <CardHeader>
            <CardTitle>Link VDR</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-neutral-600">
              Link a Virtual Data Room to this fundraise to track investor activity and integrate visitor analytics.
            </p>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Select VDR</label>
              <select
                className="w-full h-10 px-3 py-2 bg-white border border-neutral-200 rounded-lg text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                value={fundraise.linkedVdrId || ''}
                onChange={(e) => setFundraise({ ...fundraise, linkedVdrId: e.target.value || undefined })}
              >
                <option value="">No VDR linked</option>
                {vdrRooms.map(room => (
                  <option key={room.id} value={room.id}>{room.name}</option>
                ))}
              </select>
            </div>

            {fundraise.linkedVdrId && (
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="text-sm text-emerald-800">
                  ✓ VDR linked. Visitor activity from contacts at your funds will be tracked automatically.
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-neutral-100 flex justify-end">
              <Button onClick={handleSave} disabled={saving} text={saving ? 'Saving...' : 'Save Changes'} />
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'team' && !isNew && (
        <Card>
          <CardHeader>
            <CardTitle>Team Access</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Add User Form */}
            <form onSubmit={handleAddUser} className="p-4 bg-neutral-50 rounded-lg border border-neutral-100">
              <h3 className="text-sm font-medium mb-3">Add Team Member</h3>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="User email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  className="flex-1"
                />
                <select
                  className="w-32 h-9 px-3 py-1 bg-white border border-neutral-200 rounded-md text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value)}
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </select>
                <Button type="submit" text="Add" />
              </div>
            </form>

            {/* Permissions List */}
            <div>
              <h3 className="text-sm font-medium mb-3">Current Access</h3>
              {permissions.length === 0 ? (
                <p className="text-sm text-neutral-500 text-center py-4">
                  No team members added yet.
                </p>
              ) : (
                <div className="border rounded-lg divide-y">
                  {permissions.map((perm) => (
                    <div key={perm.id} className="p-3 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm">{perm.profiles?.name || 'Unknown'}</div>
                        <div className="text-xs text-neutral-500">{perm.profiles?.email}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs bg-neutral-100 px-2 py-1 rounded capitalize">
                          {perm.access_level}
                        </span>
                        <Button
                          variant="danger-outline"
                          size="sm"
                          onClick={() => handleRemoveUser(perm.user_id)}
                          text="Remove"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'team' && isNew && (
        <Card>
          <CardContent className="p-6 text-center text-neutral-500">
            Save the fundraise first to manage team access.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
